import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, useNavigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import QRCodeGenerator from './pages/qr-code-generator';
import LearningTrailsCreator from './pages/learning-journey-creator';
import SplashPage from './pages/splash-page';
import TeacherLogin from './pages/teacher-login';
import TeacherDashboard from './pages/teacher-dashboard';
import ClassroomManagement from './pages/classroom-management';
import LearningTrailDetailView from './pages/learning-trail-detail-view';
import StudentLogin from './pages/student-login';
import StudentDashboard from './pages/student-dashboard';
import QRCodeScanner from './pages/qr-code-scanner';
import StudentLearningTrailView from './pages/student-learning-trail-view';
import StudentRewardsCenter from './pages/student-rewards-center';
import TeacherRewardsManagement from './pages/teacher-rewards-management';
import TeacherSettings from './pages/teacher-settings';
import { useAuth } from './contexts/AuthContext';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/teacher-login" replace />;
  }

  return children;
};

// Student Protected Route Component
const StudentProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const session = localStorage.getItem('studentSession');
    if (!session) {
      navigate('/student-login', { replace: true });
      setLoading(false);
      return;
    }

    try {
      JSON.parse(session);
      setAuthenticated(true);
    } catch (error) {
      navigate('/student-login', { replace: true });
    }
    setLoading(false);
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return authenticated ? children : null;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* Public routes */}
        <Route path="/" element={<SplashPage />} />
        <Route path="/splash-page" element={<SplashPage />} />
        <Route path="/teacher-login" element={<TeacherLogin />} />
        <Route path="/student-login" element={<StudentLogin />} />
        
        {/* Teacher Protected routes */}
        <Route path="/teacher-dashboard" element={<ProtectedRoute><TeacherDashboard /></ProtectedRoute>} />
        <Route path="/learning-trails-creator" element={<ProtectedRoute><LearningTrailsCreator /></ProtectedRoute>} />
        <Route path="/learning-trail-detail-view/:id" element={<ProtectedRoute><LearningTrailDetailView /></ProtectedRoute>} />
        <Route path="/classroom-management" element={<ProtectedRoute><ClassroomManagement /></ProtectedRoute>} />
        <Route path="/qr-code-generator" element={<ProtectedRoute><QRCodeGenerator /></ProtectedRoute>} />
        
        {/* Student Protected routes */}
        <Route path="/student-dashboard" element={<StudentProtectedRoute><StudentDashboard /></StudentProtectedRoute>} />
        <Route path="/qr-code-scanner" element={<StudentProtectedRoute><QRCodeScanner /></StudentProtectedRoute>} />
        <Route path="/student-learning-trail-view" element={<StudentProtectedRoute><StudentLearningTrailView /></StudentProtectedRoute>} />
        <Route path="/student-rewards-center" element={<StudentProtectedRoute><StudentRewardsCenter /></StudentProtectedRoute>} />
        
        {/* Teacher Protected routes for rewards */}
        <Route path="/teacher-rewards-management" element={<ProtectedRoute><TeacherRewardsManagement /></ProtectedRoute>} />
        <Route path="/teacher-settings" element={<ProtectedRoute><TeacherSettings /></ProtectedRoute>} />
        
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
